import { Op } from 'quill-delta';

import {
  CustomNumberFormat,
  CustomNumberFormatJSON,
  MutableCustomNumberFormat,
} from './custom-number-format';

export enum ParentPageType {
  PAGE = 'PAGE',
  AUTO = 'AUTO',
  MACRO = 'MACRO',
  FIELD = 'FIELD',
}

export enum ParentPageOption {
  DYNAMIC = 'dynamic',
  FIXED = 'fixed',
}

export enum FieldType {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Checkbox = 'checkbox',
  Select = 'select',
  User = 'user',
  Attachment = 'attachment',
  PageLink = 'page',
  PageLabels = 'pageLabels',
  PageStatus = 'pageStatus',
  PageDetails = 'pageDetails',
  Hyperlink = 'hyperlink',
  EntryLink = 'entryLink',
  EntryDetails = 'entryDetails',
  Backlink = 'backlink',
  JiraIssue = 'jiraIssue',
  JiraIssueDetails = 'jiraIssueDetails',
}

// outdated, eventually remove
export enum DisplayedDetailType {
  CREATED_AT = 'createdAt',
  CREATED_BY = 'createdBy',
  LAST_UPDATED_AT = 'lastUpdatedAt',
  LAST_UPDATED_BY = 'lastUpdatedBy',
  EXCERPT_CONTENT = 'excerptContent',
  VERSION_NUMBER = 'versionNumber',
}

export enum ContentFieldToDisplayedDetailType {
  CREATED_AT = 'createdAt',
  CREATED_BY = 'createdBy',
  UPDATED_AT = 'updatedAt',
  UPDATED_BY = 'updatedBy',
  EXCERPT = 'excerpt',
  VERSION_NUMBER = 'versionNumber',
}

export enum BacklinkDisplayType {
  AS_LINK = 'asLink',
  AS_NUMBER = 'asNumber',
}
export enum NumberFormat {
  NUMBER = 'number',
  PERCENT = 'percent',
  CUSTOM = 'custom',
  DOLLAR = 'dollar',
  EURO = 'euro',
}

export enum YNumberFormat {
  NUMBER = 'n',
  PERCENT = 'p',
  DOLLAR = '$',
  EURO = 'e',
  CUSTOM = 'c',
}
export const YNumberFormatPossibleValues = Object.values(YNumberFormat);

export enum YDisplayedDetailType {
  CREATED_AT = 'ca',
  CREATED_BY = 'cb',
  LAST_UPDATED_AT = 'lua',
  LAST_UPDATED_BY = 'lub',
  EXCERPT_CONTENT = 'ec',
  VERSION_NUMBER = 'vn',
}
export const YDisplayedDetailTypePossibleValues = Object.values(YDisplayedDetailType);

export enum YBacklinkDisplayType {
  AS_LINK = 'al',
  AS_NUMBER = 'an',
}
export const YBacklinkDisplayTypePossibleValues = Object.values(YBacklinkDisplayType);

export const NumberFormatMap: Record<NumberFormat, YNumberFormat> = {
  [NumberFormat.NUMBER]: YNumberFormat.NUMBER,
  [NumberFormat.PERCENT]: YNumberFormat.PERCENT,
  [NumberFormat.DOLLAR]: YNumberFormat.DOLLAR,
  [NumberFormat.EURO]: YNumberFormat.EURO,
  [NumberFormat.CUSTOM]: YNumberFormat.CUSTOM,
};

export const YNumberFormatMap: Record<YNumberFormat, NumberFormat> = {
  [YNumberFormat.NUMBER]: NumberFormat.NUMBER,
  [YNumberFormat.PERCENT]: NumberFormat.PERCENT,
  [YNumberFormat.DOLLAR]: NumberFormat.DOLLAR,
  [YNumberFormat.EURO]: NumberFormat.EURO,
  [YNumberFormat.CUSTOM]: NumberFormat.CUSTOM,
};

export const ContentFieldToDisplayedDetailTypeMap: Record<string, DisplayedDetailType> = {
  [ContentFieldToDisplayedDetailType.CREATED_AT]: DisplayedDetailType.CREATED_AT,
  [ContentFieldToDisplayedDetailType.CREATED_BY]: DisplayedDetailType.CREATED_BY,
  [ContentFieldToDisplayedDetailType.UPDATED_AT]: DisplayedDetailType.LAST_UPDATED_AT,
  [ContentFieldToDisplayedDetailType.UPDATED_BY]: DisplayedDetailType.LAST_UPDATED_BY,
  [ContentFieldToDisplayedDetailType.EXCERPT]: DisplayedDetailType.EXCERPT_CONTENT,
  [ContentFieldToDisplayedDetailType.VERSION_NUMBER]: DisplayedDetailType.VERSION_NUMBER,
};

export const DisplayedDetailTypeToContentFieldMap: Record<DisplayedDetailType, string> = {
  [DisplayedDetailType.CREATED_AT]: ContentFieldToDisplayedDetailType.CREATED_AT,
  [DisplayedDetailType.CREATED_BY]: ContentFieldToDisplayedDetailType.CREATED_BY,
  [DisplayedDetailType.LAST_UPDATED_AT]: ContentFieldToDisplayedDetailType.UPDATED_AT,
  [DisplayedDetailType.LAST_UPDATED_BY]: ContentFieldToDisplayedDetailType.UPDATED_BY,
  [DisplayedDetailType.EXCERPT_CONTENT]: ContentFieldToDisplayedDetailType.EXCERPT,
  [DisplayedDetailType.VERSION_NUMBER]: ContentFieldToDisplayedDetailType.VERSION_NUMBER,
};

export const DisplayedDetailTypeMap: Record<DisplayedDetailType, YDisplayedDetailType> = {
  [DisplayedDetailType.CREATED_AT]: YDisplayedDetailType.CREATED_AT,
  [DisplayedDetailType.CREATED_BY]: YDisplayedDetailType.CREATED_BY,
  [DisplayedDetailType.LAST_UPDATED_AT]: YDisplayedDetailType.LAST_UPDATED_AT,
  [DisplayedDetailType.LAST_UPDATED_BY]: YDisplayedDetailType.LAST_UPDATED_BY,
  [DisplayedDetailType.EXCERPT_CONTENT]: YDisplayedDetailType.EXCERPT_CONTENT,
  [DisplayedDetailType.VERSION_NUMBER]: YDisplayedDetailType.VERSION_NUMBER,
};

export const YDisplayedDetailTypeMap: Record<YDisplayedDetailType, DisplayedDetailType> =
  {
    [YDisplayedDetailType.CREATED_AT]: DisplayedDetailType.CREATED_AT,
    [YDisplayedDetailType.CREATED_BY]: DisplayedDetailType.CREATED_BY,
    [YDisplayedDetailType.LAST_UPDATED_AT]: DisplayedDetailType.LAST_UPDATED_AT,
    [YDisplayedDetailType.LAST_UPDATED_BY]: DisplayedDetailType.LAST_UPDATED_BY,
    [YDisplayedDetailType.EXCERPT_CONTENT]: DisplayedDetailType.EXCERPT_CONTENT,
    [YDisplayedDetailType.VERSION_NUMBER]: DisplayedDetailType.VERSION_NUMBER,
  };

export const BacklinkDisplayTypeMap: Record<BacklinkDisplayType, YBacklinkDisplayType> = {
  [BacklinkDisplayType.AS_LINK]: YBacklinkDisplayType.AS_LINK,
  [BacklinkDisplayType.AS_NUMBER]: YBacklinkDisplayType.AS_NUMBER,
};

export const YBacklinkDisplayTypeMap: Record<YBacklinkDisplayType, BacklinkDisplayType> =
  {
    [YBacklinkDisplayType.AS_LINK]: BacklinkDisplayType.AS_LINK,
    [YBacklinkDisplayType.AS_NUMBER]: BacklinkDisplayType.AS_NUMBER,
  };

// Also defined in @databases/types/macros
export const selectOptionColors = [
  'blueDark',
  'redDark',
  'yellowDark',
  'greenDark',
  'tealDark',
  'purpleDark',
  'greyDark',
  'blueLight',
  'redLight',
  'yellowLight',
  'greenLight',
  'tealLight',
  'purpleLight',
  'greyLight',
] as const;

export type SelectOptionColor = (typeof selectOptionColors)[number];

export type SelectOptionJSON = {
  id: string;
  label: string;
  color: SelectOptionColor;
};

export interface SelectOption {
  get id(): string;
  get label(): string;
  get color(): SelectOptionColor;
  toJSON(): SelectOptionJSON;
}

export interface MutableSelectOption extends SelectOption {
  applyLabelDelta(delta: Op[]): void;
  set color(color: SelectOptionColor);
}

export interface SelectOptions {
  get options(): SelectOption[];
  toJSON(): SelectOptionJSON[];
}

export interface MutableSelectOptions extends SelectOptions {
  get options(): MutableSelectOption[];
  add(option: SelectOptionJSON): void;
  reorder(index: number, newIndex: number): void;
  delete(index: number): void;
}

export interface Field {
  get description(): string;
  get name(): string;
  get type(): FieldType;
  get width(): number | undefined;
  get showDescription(): boolean;

  // TODO: move into field type interfaces
  get resolve(): boolean;
  get multi(): boolean;
  get newlines(): boolean;
  get allowEdit(): boolean;
  get databaseId(): string | undefined;
  get displayedDetail(): DisplayedDetailType;
  get backlinkDisplayType(): BacklinkDisplayType;
  get fieldId(): string | undefined;
  get includeVersionComment(): boolean | undefined;
  get creatable(): boolean | undefined;
  get entryFieldField(): string | undefined;
  get pageTemplateId(): string | undefined;
  get parentPage(): string | undefined;
  get parentPageOption(): ParentPageOption;
  get isParentPageSourceDatabase(): boolean | undefined;
  get displayEntryOnPage(): boolean | undefined;
  get parentPageType(): ParentPageType;
  get parentPageFieldId(): string | undefined;
  get jiraField(): string | undefined;
  get jiraServerId(): string | undefined;
  get cloudId(): string | undefined;
  get numberFormat(): NumberFormat;
  get customNumberFormat(): CustomNumberFormat;
  get selectOptions(): SelectOptions;
  get defaultEnabled(): boolean;
  get mentionNotificationEnabled(): boolean;

  toJSON(): FieldJSON;
}

export type FieldJSON = {
  description: string;
  name: string;
  type: FieldType;
  width: number | undefined;
  showDescription: boolean;

  // TODO: move into field type interfaces
  resolve: boolean;
  multi: boolean;
  newlines: boolean;
  allowEdit: boolean;
  databaseId: string | undefined;
  displayedDetail: DisplayedDetailType | undefined;
  contentField: string | undefined;
  backlinkDisplayType: BacklinkDisplayType;
  fieldId: string | undefined;
  includeVersionComment: boolean | undefined;
  creatable: boolean | undefined;
  entryFieldField: string | undefined;
  pageTemplateId: string | undefined;
  parentPage: string | undefined;
  parentPageOption: ParentPageOption;
  isParentPageSourceDatabase: boolean | undefined;
  displayEntryOnPage: boolean | undefined;
  parentPageType: ParentPageType;
  parentPageFieldId: string | undefined;
  jiraField: string | undefined;
  jiraServerId: string | undefined;
  cloudId: string | undefined;
  numberFormat: NumberFormat;
  customNumberFormat: CustomNumberFormatJSON;
  selectOptions: SelectOptionJSON[];
  defaultEnabled: boolean;
  mentionNotificationEnabled: boolean;
};

export interface MutableField extends Field {
  applyNameDelta(delta: Op[]): void;
  applyDescriptionDelta(delta: Op[]): void;
  set type(newType: FieldType);
  set width(val: number | undefined);
  set showDescription(val: boolean);

  // TODO: move into field type interfaces
  set resolve(val: boolean);
  set multi(val: boolean);
  set newlines(val: boolean);
  set allowEdit(val: boolean);
  set displayedDetail(val: DisplayedDetailType);
  set contentField(val: string | undefined);
  set backlinkDisplayType(val: BacklinkDisplayType);
  set databaseId(val: string | undefined);
  set fieldId(val: string | undefined);
  set includeVersionComment(val: boolean | undefined);
  set creatable(val: boolean | undefined);
  set entryFieldField(val: string | undefined);
  set pageTemplateId(val: string | undefined);
  set parentPage(val: string | undefined);
  set parentPageOption(val: ParentPageOption);
  set isParentPageSourceDatabase(val: boolean | undefined);
  set displayEntryOnPage(val: boolean | undefined);
  set parentPageType(val: ParentPageType);
  set parentPageFieldId(val: string | undefined);
  set jiraField(val: string | undefined);
  set jiraServerId(val: string | undefined);
  set cloudId(val: string | undefined);
  set numberFormat(val: NumberFormat);
  get customNumberFormat(): MutableCustomNumberFormat;
  get selectOptions(): MutableSelectOptions;
  set defaultEnabled(val: boolean);
  set mentionNotificationEnabled(val: boolean);
}
